import { useSyncExternalStore } from 'react'
import { getInitialLanguage } from './initialLanguage'

/**
 * The URL :language segment as a Language enum, re-read on every navigation.
 *
 * The base LanguageWrapper sits above the RouterProvider (it also wraps the lock
 * screen and router-level loaders), so it cannot use useLocation. The router
 * navigates via history.pushState/replaceState, which emit no DOM event, so we
 * patch them once to notify subscribers (plus popstate for back/forward).
 *
 * Without this the base language froze at the first-loaded URL: arriving on a
 * language the report source does not offer, the wrapper redirects to the default
 * (e.g. /sk -> /en-us) but the base layer stayed on the original language,
 * diverging from the per-branch layer. Two LanguageProviders in different
 * languages then fought over shared state (the Apollo error copy), looping.
 */
const subscribers = new Set<() => void>()
let patched = false

const patchHistoryOnce = () => {
  if (patched) {
    return
  }
  patched = true

  const notify = () => {
    for (const subscriber of subscribers) {
      subscriber()
    }
  }

  const { pushState, replaceState } = window.history
  window.history.pushState = (...args: Parameters<History['pushState']>) => {
    pushState.apply(window.history, args)
    notify()
  }
  window.history.replaceState = (...args: Parameters<History['replaceState']>) => {
    replaceState.apply(window.history, args)
    notify()
  }
  window.addEventListener('popstate', notify)
}

const subscribe = (callback: () => void) => {
  patchHistoryOnce()
  subscribers.add(callback)
  return () => {
    subscribers.delete(callback)
  }
}

// getInitialLanguage returns a Language enum string, so useSyncExternalStore's
// Object.is check on the snapshot only re-renders when the URL language changes.
export const useUrlLanguage = () => useSyncExternalStore(subscribe, getInitialLanguage)
